import React from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    Paper,
    TextField,
    Typography,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
} from '@mui/material';
import type { Konfigurace } from '../../../api/JsdhNotifikaceService/generated';
import type { useUpdateAdminKonfiguraceMutation } from '../../../api/JsdhNotifikaceService/service/AdminMutations';
import { useAllUsersQuery } from '../../../api/JsdhNotifikaceService/service/AdminMutations';

interface NastenkaTabProps {
    konfigurace: Konfigurace[] | undefined;
    updateKonfiguraceMutation: ReturnType<typeof useUpdateAdminKonfiguraceMutation>;
    refetchKonfigurace: () => void;
}

const NastenkaTab: React.FC<NastenkaTabProps> = ({
    konfigurace,
    updateKonfiguraceMutation,
    refetchKonfigurace,
}) => {
    const { data: allUsers, isLoading: usersLoading } = useAllUsersQuery();

    const mode = konfigurace?.find(k => k.klic === 'NASTENKA_WRITE_MODE')?.hodnota || 'VSICHNI';
    const userIdsCsv = konfigurace?.find(k => k.klic === 'NASTENKA_WRITE_USER_IDS')?.hodnota || '';
    const selectedIds = userIdsCsv
        .split(',')
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !Number.isNaN(n));

    const handleModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateKonfiguraceMutation.mutate(
            { klic: 'NASTENKA_WRITE_MODE', hodnota: e.target.value },
            { onSuccess: () => refetchKonfigurace() }
        );
    };

    // POZOR: ZÁMĚRNĚ nepoužíváme komponentu UserSelector — ta je stavěná na výběr PŘÍJEMCŮ
    // NOTIFIKACÍ (filtruje u.aktivni && u.notifikaceEnabled + má switch „Odeslat všem").
    // Pro OPRÁVNĚNÍ ZÁPISU je filtr notifikaceEnabled sémanticky špatně a při změně výběru
    // by tiše vyhazoval už uložená ID členů s vypnutými notifikacemi.
    //
    // Nabídka = aktivní členové + neaktivní, kteří jsou aktuálně vybráni (aby šli vidět a odebrat).
    const options = (allUsers || []).filter(u => u.aktivni || selectedIds.includes(u.id));
    const value = options.filter(u => selectedIds.includes(u.id));

    const handleUsersChange = (newUsers: { id: number }[]) => {
        // Zachovej ID, která nejsou v allUsers vůbec (smazaný člen) — žádná tichá ztráta
        const preserved = selectedIds.filter(id => !(allUsers || []).some(u => u.id === id));
        const merged = [...preserved, ...newUsers.map(u => u.id)];
        updateKonfiguraceMutation.mutate(
            { klic: 'NASTENKA_WRITE_USER_IDS', hodnota: merged.join(',') },
            { onSuccess: () => refetchKonfigurace() }
        );
    };

    return (
        <Paper sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Nástěnka — kdo smí přidávat příspěvky
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Admin smí přidávat vždy. Oprávnění vyhodnocuje backend — mobilní aplikace i web
                jen respektují vrácený příznak.
            </Typography>

            <FormControl disabled={updateKonfiguraceMutation.isPending}>
                <RadioGroup value={mode} onChange={handleModeChange}>
                    <FormControlLabel value="VSICHNI" control={<Radio />} label="Všichni členové" />
                    <FormControlLabel value="VELITELE" control={<Radio />} label="Jen velitelé (funkce VD)" />
                    <FormControlLabel value="VYBRANI" control={<Radio />} label="Jen vybraní členové" />
                </RadioGroup>
            </FormControl>

            {mode === 'VYBRANI' && (
                <Box sx={{ mt: 2, maxWidth: 500 }}>
                    <Autocomplete
                        multiple
                        options={options}
                        value={value}
                        onChange={(_, newValue) => handleUsersChange(newValue)}
                        loading={usersLoading}
                        disabled={updateKonfiguraceMutation.isPending}
                        getOptionLabel={(option) => option.aktivni ? option.jmeno : `${option.jmeno} (neaktivní)`}
                        isOptionEqualToValue={(option, v) => option.id === v.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Členové s právem přidávat"
                                placeholder="Začněte psát jméno..."
                                size="small"
                            />
                        )}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => {
                                const { key, ...rest } = getTagProps({ index });
                                const label = option.aktivni ? option.jmeno : `${option.jmeno} (neaktivní)`;
                                return <Chip key={key} label={label} size="small" {...rest} />;
                            })
                        }
                        noOptionsText="Žádní aktivní členové"
                    />
                    {(() => {
                        const orphanIds = selectedIds.filter(id => !(allUsers || []).some(u => u.id === id));
                        return orphanIds.length > 0 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                +{orphanIds.length} oprávnění patří členům, kteří už v systému nejsou.
                            </Typography>
                        ) : null;
                    })()}
                </Box>
            )}
        </Paper>
    );
};

export default NastenkaTab;
